'use client';

import { createContext, use, useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { api, ApiRequestError } from '@/lib/api';
import { resetOrgSettings } from '@/lib/org-settings';
import { rememberSatelliteAvailable } from '@/lib/basemap-preference';

/** Backoff schedule for retrying a transient /auth/me failure before giving up. */
const ME_RETRY_DELAYS_MS = [1_000, 3_000, 6_000];

interface User {
  id: string;
  email: string;
  full_name: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  /** IANA zone name (e.g. "Asia/Jerusalem"). Absent on an API older than this field
   *  reads as undefined — every display formatter treats that the same as 'UTC'. */
  timezone?: string;
}

/**
 * Deployment-level switches, read once per load from `/api/auth/me`. Not org
 * settings (an admin cannot flip them) and not build variables (dev and prod
 * are different API containers with different env files, so they can differ
 * without a frontend rebuild). Absent on the wire, every switch is off.
 */
export interface AuthFeatures {
  /** "Video beside the map" outside a manual session (#505 note 4). */
  copSplitVideo: boolean;
  /**
   * Whether this deployment can serve the satellite basemap - i.e. whether the
   * API has a Google Map Tiles key. Off means the Layers menu simply does not
   * offer imagery, which is honest; offering it and then blanking the map when
   * it is picked is not.
   */
  satelliteBasemap: boolean;
}

const NO_FEATURES: AuthFeatures = { copSplitVideo: false, satelliteBasemap: false };

interface AuthState {
  user: User | null;
  organization: Organization | null;
  role: 'admin' | 'operator' | 'viewer' | null;
  isSuperAdmin: boolean;
  features: AuthFeatures;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, fullName: string, orgName: string) => Promise<void>;
  /**
   * After login/accept-invite. Only the access token is needed - the refresh
   * token is delivered as an httpOnly cookie, so the second arg is optional and
   * ignored by the implementation.
   */
  establishSession: (accessToken: string, refreshToken?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { push } = useRouter();
  const [state, setState] = useState<AuthState>({
    user: null,
    organization: null,
    role: null,
    isSuperAdmin: false,
    features: NO_FEATURES,
    loading: true,
  });

  const meRetryTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const loadUser = useCallback(async (retryAttempt = 0) => {
    if (!api.isAuthenticated) {
      setState({ user: null, organization: null, role: null, isSuperAdmin: false, features: NO_FEATURES, loading: false });
      return;
    }
    try {
      const data = await api.get<{
        user: User;
        organization: Organization | null;
        role: 'admin' | 'operator' | 'viewer';
        is_superadmin?: boolean;
        /** Optional: an API older than the switch sends nothing, which reads as off. */
        features?: { cop_split_video?: boolean; satellite_basemap?: boolean };
      }>('/api/auth/me');
      // Cached for the six maps that pick a basemap synchronously as they are
      // constructed, long before this answer would otherwise reach them. Written
      // only where the API has actually answered: a signed-out browser knows
      // nothing about the deployment and must not overwrite what it learned.
      rememberSatelliteAvailable(data.features?.satellite_basemap === true);
      setState({
        user: data.user,
        organization: data.organization,
        role: data.role,
        isSuperAdmin: !!data.is_superadmin,
        features: {
          copSplitVideo: data.features?.cop_split_video === true,
          satelliteBasemap: data.features?.satellite_basemap === true,
        },
        loading: false,
      });
    } catch (err) {
      // Only a genuine auth rejection (invalid/expired session - the API client
      // already tried a silent token refresh before this) means "log out."
      // Anything else (rate limit, 5xx, network blip) is transient: retry with
      // backoff and keep the session tokens intact, so a momentary hiccup never
      // force-logs-out an operator mid-shift - the exact silent-failure class
      // Sprint 2 hardens against.
      const isAuthRejection = err instanceof ApiRequestError && (err.status === 401 || err.status === 403);
      if (isAuthRejection) {
        api.clearTokens();
        // Same reason as the explicit `logout` below: this is an identity
        // ending. A forced sign-out leaves the operator on `/login` with an
        // `/api/org` possibly still open under the token just cleared, and the
        // next person to sign in on this console must not be handed it.
        resetOrgSettings();
        setState({ user: null, organization: null, role: null, isSuperAdmin: false, features: NO_FEATURES, loading: false });
        return;
      }
      console.warn(`[auth] /auth/me failed transiently (attempt ${retryAttempt + 1}):`, err);
      if (retryAttempt < ME_RETRY_DELAYS_MS.length) {
        meRetryTimeout.current = setTimeout(() => { void loadUser(retryAttempt + 1); }, ME_RETRY_DELAYS_MS[retryAttempt]);
        return;
      }
      // Retries exhausted - stop the loading spinner without clearing tokens,
      // so the next natural reload (once the transient issue clears) logs the
      // operator straight back in instead of forcing a fresh sign-in.
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadUser();
    return () => clearTimeout(meRetryTimeout.current);
  }, [loadUser]);

  const establishSession = useCallback(async (accessToken: string, _refreshToken?: string) => {
    // Before the token, not after: this is the moment the identity changes, and
    // it is the ONE path every sign-in takes (login, signup and the MFA flow all
    // land here). An `/api/org` still open from whoever was signed in a moment
    // ago would otherwise answer the mount that follows `push('/')` below,
    // handing this operator the previous one's altitude ceiling and presets -
    // which drive the GO-TO bar and the command bar's takeoff altitude.
    resetOrgSettings();
    api.setTokens(accessToken);
    await loadUser();
    push('/');
  }, [loadUser, push]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<{
      user: User;
      org_id: string;
      role: 'admin' | 'operator' | 'viewer';
      access_token: string;
      refresh_token: string;
    }>('/api/auth/login', { email, password });

    await establishSession(data.access_token, data.refresh_token);
  }, [establishSession]);

  const signup = useCallback(async (email: string, password: string, fullName: string, orgName: string) => {
    const data = await api.post<{
      user: User;
      organization: Organization;
      access_token: string;
      refresh_token: string;
    }>('/api/auth/signup', {
      email,
      password,
      full_name: fullName,
      org_name: orgName,
    });

    await establishSession(data.access_token, data.refresh_token);
  }, [establishSession]);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', {});
    } catch {
      // ignore - clear tokens regardless
    }
    api.clearTokens();
    // The shared `/api/org` entry is not keyed to an identity, so a request still
    // open under this operator's token must not be handed to the next one who
    // logs in on this console - it carries the altitude ceiling and presets the
    // GO-TO bar and the command bar fly to.
    resetOrgSettings();
    setState({ user: null, organization: null, role: null, isSuperAdmin: false, features: NO_FEATURES, loading: false });
    push('/login');
  }, [push]);

  const value = useMemo(
    () => ({ ...state, login, signup, establishSession, logout }),
    [state, login, signup, establishSession, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Like `useAuth`, but returns `null` outside an `AuthProvider` instead of throwing.
 * For a value that has a sane default when identity is unknown (e.g. the org's
 * display timezone falling back to UTC) — not for anything that needs a real
 * identity to make sense, which should keep using `useAuth` and its hard failure.
 */
export function useAuthOptional() {
  return use(AuthContext);
}

export { ApiRequestError };
